import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { SecondaryTabs } from '../../3-pattern';
import { RebalancePages } from './components/shared/sharedTypes';
import RebalancePage from './components/rebalancePage';
import SubTradeRequestsPage, { SubTradeRequestGroupStates } from './components/subTradeRequestsPage';
import PortfolioAdjustmentsModal from '../rebalanceReport/components/modal';
import { isGoal } from './components/shared/rebalanceColumns';
import { Box, Typography } from '../../1-primative';
import { Switch } from '../../2-component';
import { PageObjectType } from '../pageConfiguration/pageConfiguration';

// Mapping from tab accessor to SubTradeRequestGroupStates
const subTradeTabs = ['pending', 'requested', 'unsuccessful', 'complete'] as const;
type SubTradeTab = typeof subTradeTabs[number];
const tabToSubTradeState: Record<SubTradeTab, SubTradeRequestGroupStates> = {
  pending: 'PENDING',
  requested: 'REQUESTED',
  unsuccessful: 'UNSUCCESSFUL',
  complete: 'COMPLETE',
};

const RebalanceReportV2 = () => {
  const { t } = useTranslation(['rebalanceReportV2']);
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('rebalance');
  const [modalOpen, setModalOpen] = useState(false);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [useInlinePortfolioTable, setUseInlinePortfolioTable] = useState(true);

  const TABS = [
    {
      accessor: 'rebalance',
      page: RebalancePages.rebalance,
      name: t('tabs.rebalance'),
      notify: 4,
    },
    {
      accessor: 'rebalance_ui',
      page: RebalancePages.rebalance_ui,
      name: t('tabs.rebalance_ui'),
      notify: 0,
    },
    {
      accessor: 'pending',
      page: RebalancePages.pending,
      name: t('tabs.pending'),
      notify: 0,
    },
    {
      accessor: 'requested',
      page: RebalancePages.requested,
      name: t('tabs.requested'),
      notify: 0,
    },
    {
      accessor: 'unsuccessful',
      page: RebalancePages.unsuccessful,
      name: t('tabs.unsuccessful'),
      notify: 0,
    },
    {
      accessor: 'complete',
      page: RebalancePages.complete,
      name: t('tabs.complete'),
      notify: 0,
    },
  ];

  const handleRowClick = (row: any) => {
    if (currentTab === 'rebalance') {
      setActiveItem({
        type: isGoal(row) ? PageObjectType.GOAL : PageObjectType.SUB_ACCOUNT,
        id: isGoal(row)
          ? (!isEmpty(row.object?.goal) ? row.object.goal.id : row.object.id)
          : row.object.id,
        portfolioReportId: row.id,
      });
      if (!useInlinePortfolioTable) {
        setModalOpen(true);
      } else if (isGoal(row)) {
        navigate(`/rebalanceReportV2/goal/${!isEmpty(row.object.goal) ? row.object.goal.id : row.object.id}`);
      } else {
        navigate(`/rebalanceReportV2/subAccount/${row.object.id}`);
      }
    }
    // For non-rebalance tabs, do nothing
  };

  const handleTabChange = (tab: string | number) => {
    setCurrentTab(tab as string);
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 'rebalance':
        return <RebalancePage useInlinePortfolioTable={useInlinePortfolioTable} />;
      case 'rebalance_ui':
        return <div>{t('tabs.rebalance_ui')}</div>;
      case 'pending':
      case 'requested':
      case 'unsuccessful':
      case 'complete':
        return (
          <SubTradeRequestsPage
            state={tabToSubTradeState[currentTab as SubTradeTab]}
            onTableRowClick={handleRowClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Box display='flex' justifyContent='flex-end' alignItems='center' mb={1}>
        <Typography variant='bodySmall' mr={1}>{t('portfolio.newPortfolioBlotter')}</Typography>
        <Switch
          checked={useInlinePortfolioTable}
          onChange={() => setUseInlinePortfolioTable((v) => !v)}
        />
      </Box>
      <SecondaryTabs
        tabs={TABS.map((tab) => ({
          label: tab.name,
          icon: '',
          key: tab.accessor,
        }))}
        activeTab={currentTab}
        setActiveTab={handleTabChange}
      />
      {renderTabContent()}
      {!useInlinePortfolioTable && activeItem && modalOpen && (
        <PortfolioAdjustmentsModal
          item={activeItem}
          open={modalOpen}
          handleClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default RebalanceReportV2;
